import {Button, Grid, Typography} from '@mui/material';
import React, {useEffect} from 'react';
import {Box} from '@mui/system';
import {useSnackbar} from 'notistack';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  ReportPackGenericFormType,
  reportPackFormType,
  ReportPackGenericFormInnerType,
  reportPackTaxYearField,
  reportPackIsaFormSubscriptionTypeField,
  reportPackIsaAmountField,
} from './ReportPackFields';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../shared/UPDATES';
import {useReportPack} from './useReportPack';
import {getNumberOrNull} from '../../utils/getNumberOrNull';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {getReportPackMissingFieldError} from './getReportPackMissingFieldError';
import {getTaxyear} from '../../utils/getTaxYear';

export const ReportPackISAApplication: React.FC<ReportPackGenericFormType> = ({
  formData,
  onClose,
  disabled,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {onUpsertIsaApplicationForm} = useReportPack();
  return (
    <ReportPackISAApplicationFormWrapped
      formFields={[]}
      disabled={disabled}
      submit={async (values) => {
        if (disabled) {
          return;
        }
        closeSnackbar();
        enqueueSnackbar(`Saving ${formData.formTypeLabel}...`, {
          variant: 'info',
        });
        await onUpsertIsaApplicationForm({
          variables: {
            contactCuroId: values.contactCuroId,
            reportPackId: String(formData.reportPackId),
            formId: formData.id,
            isaFormSubscriptionType: values.isaFormSubscriptionType,
            taxYear: values.taxYear,
            isaAmount: getNumberOrNull(values.isaAmount),
          },
        })
          .then(({data}) => {
            if (data?.upsertIsaApplicationForm?.ok) {
              closeSnackbar();
              enqueueSnackbar(`Successfully saved ${formData.formTypeLabel}`, {
                variant: 'success',
              });
              onClose();
            } else {
              throw Error(data?.upsertIsaApplicationForm?.errors?.join(','));
            }
          })
          .catch((err) => {
            closeSnackbar();
            enqueueSnackbar(`Could not save ${formData.formTypeLabel} ${err}`, {
              variant: 'error',
            });
          });
      }}
      initialValueOverrides={{
        ...formData,
        reportPackId: String(formData.reportPackId),
        contactCuroId: String(formData.contactCuroId),
      }}
    />
  );
};

const ReportPackISAApplicationForm: React.FC<ReportPackGenericFormInnerType> = ({
  disabled,
}) => {
  const {submitForm, isSubmitting, values, setValues} =
    useAutoForm<ReportPackFormFragment>();

  useEffect(() => {
    if (values.taxYear) {
      return;
    }

    const taxYear = getTaxyear(new Date());
    const formattedTaxYear = `${taxYear.endYear}/${taxYear.startYear
      ?.toString()
      .slice(-2)}`;

    setValues(UPDATES._2200, (previous) => {
      return {
        ...previous,
        taxYear: formattedTaxYear,
      };
    });
  }, [values.taxYear]);

  return (
    <Box maxWidth={500}>
      <Typography>ISA Aplication details:</Typography>
      <Grid container p={2} mb={2} spacing={2} direction="column">
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackTaxYearField.id)}
            field={reportPackTaxYearField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(
              values,
              reportPackIsaFormSubscriptionTypeField.id
            )}
            field={reportPackIsaFormSubscriptionTypeField}
          />
        </Grid>
        <Grid item>
          <AutoField
            readOnly={disabled}
            error={getReportPackMissingFieldError(values, reportPackIsaAmountField.id)}
            field={reportPackIsaAmountField}
          />
        </Grid>
      </Grid>
      <Box position="absolute" bottom={20} right={20}>
        {!disabled && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            onClick={() => submitForm()}
          >
            Save form
          </Button>
        )}
      </Box>
    </Box>
  );
};

const ReportPackISAApplicationFormWrapped = withAutoFormNew<
  reportPackFormType,
  ReportPackGenericFormInnerType
>(ReportPackISAApplicationForm, {}, {debugPosition: 'bottom'});
